(() => {
  const PARAM_KEY = 'faq';
  const SECTION_SELECTOR = '[data-faq-search-section]';
  const TITLE_QUERY_MAX_CHARS = 48;

  const getFaqSectionsBelow = (rootSection) => {
    const faqSections = [];
    let sibling = rootSection.nextElementSibling;

    while (sibling) {
      if (sibling.matches('.woolnut-faq') || sibling.querySelector('.woolnut-faq')) {
        faqSections.push(sibling);
      }
      sibling = sibling.nextElementSibling;
    }

    return faqSections;
  };

  const getFaqSections = (rootSection, sectionContainer) => {
    const scoped = sectionContainer.querySelectorAll('[data-faq-search-scope]');
    if (scoped.length) {
      return Array.from(scoped);
    }

    return getFaqSectionsBelow(rootSection);
  };

  const collectFaqItems = (faqSections) => {
    return faqSections.flatMap((sectionEl) => {
      return Array.from(sectionEl.querySelectorAll('.woolnut-faq__toggle')).map((toggle) => {
        // Prefer original questions over common-question clones for search + copy-link targets.
        if (toggle.closest('[data-help-common-list]')) return null;

        const titleEl = toggle.querySelector('.toggle__title');
        const contentEl = toggle.querySelector('.toggle__content');
        const keywordsText = (toggle.getAttribute('data-faq-keywords') || '').trim();

        return {
          titleText: titleEl ? titleEl.textContent.trim() : '',
          contentText: contentEl ? contentEl.textContent.trim() : '',
          keywordsText,
          toggleEl: toggle
        };
      });
    }).filter((item) => item && (item.titleText || item.contentText));
  };

  const truncateForTitle = (query) => {
    if (query.length <= TITLE_QUERY_MAX_CHARS) return query;
    return `${query.slice(0, TITLE_QUERY_MAX_CHARS).trimEnd()}...`;
  };

  const normalizeText = (value) => {
    return String(value)
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const getSearchTokens = (query) => {
    return normalizeText(query).split(' ').filter(Boolean);
  };

  const initSection = (sectionContainer) => {
    if (!sectionContainer || sectionContainer.dataset.faqSearchInitialized === 'true') return;
    sectionContainer.dataset.faqSearchInitialized = 'true';

    const input = sectionContainer.querySelector('[data-faq-search-input]');
    const clearButton = sectionContainer.querySelector('[data-faq-search-clear]');
    const resultsBox = sectionContainer.querySelector('[data-faq-search-results]');
    const resultsTitle = sectionContainer.querySelector('[data-faq-search-title]');
    const resultsList = sectionContainer.querySelector('[data-faq-search-results-list]');
    const emptyState = sectionContainer.querySelector('[data-faq-search-empty]');
    const rootSection = sectionContainer.closest('[id^="shopify-section-"]');

    if (!input || !clearButton || !resultsBox || !resultsTitle || !resultsList || !emptyState || !rootSection) return;

    const sectionId = sectionContainer.dataset.sectionId || '';
    const faqSections = getFaqSections(rootSection, sectionContainer);
    const faqItems = collectFaqItems(faqSections);
    const defaultEmptyText = emptyState.textContent.trim() || 'We couldn\'t find any matching questions. Try another search term.';

    const setFaqVisibility = (isSearchActive) => {
      faqSections.forEach((sectionEl) => {
        sectionEl.hidden = isSearchActive;
        sectionEl.style.display = isSearchActive ? 'none' : '';
      });
      resultsBox.hidden = !isSearchActive;
    };

    const setUrlQuery = (query) => {
      const url = new URL(window.location.href);
      if (query) {
        url.searchParams.set(PARAM_KEY, query);
      } else {
        url.searchParams.delete(PARAM_KEY);
      }
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    };

    const hasActiveQuery = (value) => value.trim().length > 0;

    const setClearVisibility = (query) => {
      const isVisible = hasActiveQuery(query);
      clearButton.hidden = !isVisible;
      clearButton.setAttribute('aria-hidden', String(!isVisible));
      clearButton.tabIndex = isVisible ? 0 : -1;
    };

    const renderResults = (rawQuery) => {
      const query = rawQuery.trim();
      const tokens = getSearchTokens(query);
      setClearVisibility(query);

      if (!tokens.length) {
        resultsList.innerHTML = '';
        emptyState.textContent = defaultEmptyText;
        emptyState.hidden = true;
        resultsTitle.textContent = 'Questions';
        resultsTitle.hidden = false;
        setFaqVisibility(false);
        setUrlQuery('');
        return;
      }

      const matches = faqItems.filter((item) => {
        const haystack = normalizeText(`${item.titleText} ${item.keywordsText}`);
        return tokens.every((token) => haystack.includes(token));
      });
      const uniqueMatches = [];
      const seenKeys = new Set();
      matches.forEach((item) => {
        const key = `${item.titleText.toLowerCase()}|${item.contentText.toLowerCase()}`;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          uniqueMatches.push(item);
        }
      });

      setFaqVisibility(true);
      setUrlQuery(query);
      resultsTitle.hidden = false;
      resultsTitle.textContent = `Questions about "${truncateForTitle(query)}"`;

      if (!uniqueMatches.length) {
        resultsList.innerHTML = '';
        emptyState.textContent = defaultEmptyText;
        emptyState.hidden = false;
        return;
      }

      emptyState.hidden = true;
      resultsList.innerHTML = '';

      const wrapResults = Boolean(sectionContainer.closest('.woolnut-help-center'));
      const fragment = document.createDocumentFragment();
      uniqueMatches.forEach((item, index) => {
        const clone = item.toggleEl.cloneNode(true);
        const titleEl = clone.querySelector('.toggle__title');
        const contentEl = clone.querySelector('.toggle__content');
        const sourceSlug = item.toggleEl.getAttribute('data-help-slug') || item.toggleEl.id;

        clone.classList.remove('toggle--opened');
        clone.removeAttribute('data-help-common-source');
        // Avoid duplicate IDs; keep href pointing at the original question for copy-link.
        clone.removeAttribute('id');

        if (titleEl && contentEl) {
          const contentId = `faq-search-toggle-${sectionId}-${index}`;
          titleEl.classList.remove('opened');
          titleEl.setAttribute('aria-expanded', 'false');
          titleEl.setAttribute('aria-controls', contentId);
          if (titleEl.tagName === 'A' && sourceSlug) {
            titleEl.setAttribute('href', `#${sourceSlug}`);
          }
          contentEl.id = contentId;
          contentEl.style.display = 'none';
        }

        if (wrapResults) {
          const itemEl = document.createElement('div');
          itemEl.className = 'woolnut-help-center__item';
          itemEl.appendChild(clone);

          const hr = document.createElement('hr');
          hr.className = 'woolnut-help-center__separator';
          hr.setAttribute('aria-hidden', 'true');
          itemEl.appendChild(hr);

          fragment.appendChild(itemEl);
        } else {
          fragment.appendChild(clone);
        }
      });

      resultsList.appendChild(fragment);
    };

    input.addEventListener('input', () => {
      renderResults(input.value);
    });

    clearButton.addEventListener('click', () => {
      input.value = '';
      renderResults('');
      input.focus();
    });

    const initialValue = new URL(window.location.href).searchParams.get(PARAM_KEY) || '';
    if (initialValue) {
      input.value = initialValue;
      renderResults(initialValue);
    } else {
      setClearVisibility(input.value);
    }
  };

  const initAll = () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });
})();
